// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * The live tuning dial — a −50…+50 cents gauge with an in-tune band and a
 * needle. Drawing only; all state comes in as a PitchReading.
 */

import type { PitchReading } from './types';

const FLAT = '#5aa2ff';
const IN_TUNE = '#37d67a';
const SHARP = '#ffb020';
const MUTED = '#3a3550';
const IN_TUNE_CENTS = 5;
const RANGE = 50; // cents each side

export function tierColor(cents: number): string {
  if (Math.abs(cents) <= IN_TUNE_CENTS) return IN_TUNE;
  return cents < 0 ? FLAT : SHARP;
}

/** Size a canvas to its CSS box × devicePixelRatio; returns the 2D context. */
export function fitCanvas(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
  const w = Math.max(1, Math.floor(rect.width));
  const h = Math.max(1, Math.floor(rect.height));
  if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h };
}

export function drawDial(canvas: HTMLCanvasElement, reading: PitchReading): void {
  const fit = fitCanvas(canvas);
  if (!fit) return;
  const { ctx, w, h } = fit;
  ctx.clearRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h * 0.92;
  const radius = Math.min(w * 0.46, h * 0.82);

  // cents → angle. −50 at 180°+? We span 150° total (−75°..+75° from vertical).
  const spanDeg = 75;
  const centsToAngle = (cents: number): number => {
    const clamped = Math.max(-RANGE, Math.min(RANGE, cents));
    const deg = (clamped / RANGE) * spanDeg; // −75..+75
    return (-90 + deg) * (Math.PI / 180); // -90° = straight up
  };

  // Arc background.
  ctx.lineWidth = Math.max(6, radius * 0.06);
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, centsToAngle(-RANGE), centsToAngle(RANGE));
  ctx.stroke();

  // In-tune band highlight.
  ctx.strokeStyle = 'rgba(55,214,122,0.55)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, centsToAngle(-IN_TUNE_CENTS), centsToAngle(IN_TUNE_CENTS));
  ctx.stroke();

  // Ticks.
  ctx.strokeStyle = 'rgba(255,255,255,0.22)';
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = `${Math.round(radius * 0.09)}px -apple-system, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let c = -RANGE; c <= RANGE; c += 10) {
    const a = centsToAngle(c);
    const major = c % 50 === 0 || c === 0;
    const rIn = radius - (major ? radius * 0.13 : radius * 0.08);
    ctx.lineWidth = major ? 2.5 : 1.5;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * rIn, cy + Math.sin(a) * rIn);
    ctx.lineTo(cx + Math.cos(a) * (radius - radius * 0.03), cy + Math.sin(a) * (radius - radius * 0.03));
    ctx.stroke();
    if (major) {
      const rl = radius - radius * 0.24;
      const label = c === 0 ? '0' : c > 0 ? `+${c}` : `−${Math.abs(c)}`;
      ctx.fillText(label, cx + Math.cos(a) * rl, cy + Math.sin(a) * rl);
    }
  }

  // Needle.
  const hasNote = reading.note !== null && reading.clarity > 0;
  const cents = reading.note ? reading.note.cents : 0;
  const angle = centsToAngle(cents);
  const color = hasNote ? tierColor(cents) : MUTED;
  const needleLen = radius - radius * 0.05;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, radius * 0.03);
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(angle) * needleLen, cy + Math.sin(angle) * needleLen);
  ctx.stroke();

  // Hub.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, Math.max(5, radius * 0.045), 0, Math.PI * 2);
  ctx.fill();
}
